import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

import moment from "moment";

import {
  submitExperience,
  editExperience,
  toggleAddExperience,
  toggleEditExperience,
  deleteExperience,
} from "@/store/profile/profileActions";
import type { ExperienceRecord } from "./ExperienceEntry";
import {
  experienceDateRowSx,
  experienceFormSx,
  panelHeadSx,
} from "../styles";

type ExperienceFormValues = Omit<ExperienceRecord, "id">;

const defaultEntry: ExperienceFormValues = {
  start_date: null,
  end_date: null,
  present: null,
  title: null,
  organization_name: null,
  description: null,
};

const DATE_FORMAT = "MMM DD, YYYY";

interface ExperienceFormProps {
  entry?: ExperienceRecord;
  formId?: number;
  formType?: "new" | "edit";
}

const ExperienceForm = ({
  entry,
  formId,
  formType = "new",
}: ExperienceFormProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const { register, handleSubmit, control, watch, reset } =
    useForm<ExperienceFormValues>({ defaultValues: entry || defaultEntry });

  useEffect(() => {
    reset(entry || defaultEntry);
  }, [entry, reset]);

  const presentChecked = watch("present");

  const onSubmit = (values: ExperienceFormValues) => {
    if (formType === "new") {
      dispatch(submitExperience(values));
      dispatch(toggleAddExperience());
    } else {
      dispatch(editExperience(formId, { ...entry, ...values }));
      dispatch(toggleEditExperience(null));
    }
  };

  const close = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(formType === "new" ? toggleAddExperience() : toggleEditExperience(null));
  };

  const removeExperience = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(deleteExperience(formId));
    dispatch(toggleEditExperience(null));
  };

  // TextField puts its own ref on the root, so register()'s ref has to be
  // handed to the control it wraps. Checkbox is different again: MUI 9 dropped
  // `inputRef` and its props do not accept register()'s validation attributes,
  // so the whole registration goes to the input slot.
  const presentRegistration = register("present");
  const { ref: titleRef, ...titleProps } = register("title");
  const { ref: organizationRef, ...organizationProps } =
    register("organization_name");
  const { ref: descriptionRef, ...descriptionProps } = register("description");

  return (
    <Box
      component="form"
      sx={experienceFormSx}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box sx={panelHeadSx}>
        <Typography variant="h3">
          {formType === "new" ? t("Add") : t("Edit")} {t("Experience")}
        </Typography>
        <IconButton
          aria-label={t("Experience")}
          onClick={close}
          size="small"
          sx={{ color: "grey.600" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Stack spacing={3}>
        <Box sx={experienceDateRowSx}>
          <Controller
            name="start_date"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePicker
                label={t("StartDate")}
                format={DATE_FORMAT}
                value={value ? moment(value) : null}
                // the store keeps these as dates, so the moment the picker
                // hands back is unwrapped before it reaches form state
                onChange={(next) => onChange(next ? next.toDate() : null)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            )}
          />
          <Stack spacing={1} sx={{ width: "100%" }}>
            {!presentChecked && (
              <Controller
                name="end_date"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    label={t("EndDate")}
                    format={DATE_FORMAT}
                    value={value ? moment(value) : null}
                    onChange={(next) => onChange(next ? next.toDate() : null)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(presentChecked)}
                  slotProps={{ input: presentRegistration }}
                />
              }
              label={t("Present")}
            />
          </Stack>
        </Box>

        <TextField
          {...titleProps}
          inputRef={titleRef}
          fullWidth
          required
          label={t("Title")}
          placeholder={t("ExperienceTitle")}
        />

        <TextField
          {...organizationProps}
          inputRef={organizationRef}
          fullWidth
          required
          label={t("CompanyOrSchool")}
          placeholder={t("AffiliatedCompanyOrSchool")}
        />

        <TextField
          {...descriptionProps}
          inputRef={descriptionRef}
          fullWidth
          multiline
          minRows={4}
          label={t("Description")}
          placeholder={t("ShortDescription")}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ justifyContent: "flex-end" }}
        >
          {formType === "edit" && (
            <Button
              onClick={removeExperience}
              color="error"
              size="small"
              startIcon={<DeleteOutlinedIcon fontSize="small" />}
            >
              {t("Delete")}
            </Button>
          )}
          <Button type="submit" variant="contained" size="small" disableElevation>
            {t("save")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ExperienceForm;
